import React, { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Routes as AppRoutes } from '../../../routes';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Business, CheckCircle, Error } from '@mui/icons-material';
import { getErrorMessage } from '@billda/shared-ui';
import { switchOrganization } from '../../auth/api/authApi';
import { useAuthStore } from '../../auth/stores/authStore';
import { useMultiAccountStore } from '../../auth/stores/multiAccountStore';
import { useQueryClient } from '@tanstack/react-query';
// Import all organization-specific stores that need clearing
import { useSiteImportUIStore } from '../../sites/stores/siteImportUIStore';
import { useUserManagementDataStore } from '../../users/stores/userManagementDataStore';

const OrganizationSwitching: React.FC = () => {
  const { orgId, orgName } = useParams<{ orgId: string; orgName: string }>();
  const navigate = useNavigate();
  const { setTokens } = useAuthStore();
  const queryClient = useQueryClient();

  // Organization-specific stores that need clearing
  const { resetNavState: resetSiteImportNav } = useSiteImportUIStore();
  const userManagementStore = useUserManagementDataStore();

  const [status, setStatus] = useState<
    'confirming' | 'switching' | 'success' | 'error'
  >('confirming');
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(true);
  const hasSwitchedRef = useRef(false);
  const currentOrgIdRef = useRef<string | null>(null);

  // Function to clear all organization-specific state
  const clearAllOrganizationState = () => {
    // Clear React Query cache for organization data (includes sites list)
    // Note: Features query will refetch automatically due to tokenIdentifier-based key
    queryClient.removeQueries();

    // Clear site import nav state
    resetSiteImportNav();

    // Clear user-related state
    userManagementStore.clearError();
  };

  const performSwitch = async () => {
    if (!orgId) {
      setStatus('error');
      setError('Missing organization ID');
      return;
    }

    // Mark as switching to prevent duplicate calls
    hasSwitchedRef.current = true;
    currentOrgIdRef.current = orgId;

    try {
      setStatus('switching');
      setError(null);

      const { getCurrentAccount } = useMultiAccountStore.getState();
      const currentAccount = getCurrentAccount();
      const currentRefreshToken = currentAccount?.refreshToken;

      if (!currentRefreshToken) {
        throw new globalThis.Error('Missing refresh token');
      }

      clearAllOrganizationState();

      // Call the switch organization API
      const response = await switchOrganization({
        organizationId: orgId,
        refreshToken: currentRefreshToken,
      });

      // Update the tokens in the auth store
      setTokens({
        token: response.identityToken,
        refreshToken: response.refreshToken,
      });

      // Update the stored account with the new organization name
      const updatedAccount = useMultiAccountStore
        .getState()
        .getCurrentAccount();
      if (updatedAccount && orgName) {
        useMultiAccountStore.getState().updateAccount(updatedAccount.email, {
          currentOrganisationName: decodeURIComponent(orgName),
        });
      }

      setStatus('success');

      // Redirect to home after a short delay
      setTimeout(() => {
        setIsOpen(false);
        navigate(AppRoutes.root);
      }, 1500);
    } catch (err) {
      console.error('Organization switch error:', err);
      setStatus('error');
      setError(getErrorMessage(err));
      // Reset the flag on error so retry can work
      hasSwitchedRef.current = false;
    }
  };

  const handleConfirmSwitch = () => {
    performSwitch();
  };

  const handleClose = () => {
    setIsOpen(false);
    navigate(-1);
  };

  const handleRetry = () => {
    hasSwitchedRef.current = false;
    currentOrgIdRef.current = null;
    setStatus('confirming');
    setError(null);
  };

  const renderContent = () => {
    switch (status) {
      case 'confirming':
        return (
          <>
            <DialogTitle>
              <Typography
                variant="h5"
                component="div"
                align="center"
                color="primary"
              >
                Switch Organization
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap={2}
                sx={{ pt: 2 }}
              >
                <Typography variant="h6" align="center">
                  Are you sure you want to switch to:
                </Typography>
                <Typography
                  variant="h5"
                  align="center"
                  color="secondary"
                  sx={{ fontWeight: 'bold' }}
                >
                  {orgName}
                </Typography>
                <Typography
                  variant="body1"
                  align="center"
                  color="textSecondary"
                >
                  This will change your current organization context and may
                  affect your access to certain features.
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3 }}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleClose}
                size="large"
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleConfirmSwitch}
                size="large"
              >
                Yes, Switch Organization
              </Button>
            </DialogActions>
          </>
        );

      case 'switching':
        return (
          <>
            <DialogTitle>
              <Typography
                variant="h5"
                component="div"
                align="center"
                color="primary"
              >
                Switching Organization
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap={2}
                sx={{ pt: 2, pb: 2 }}
              >
                <CircularProgress color="primary" size={60} />
                <Typography variant="h6" align="center">
                  Switching to {orgName}...
                </Typography>
                <Typography
                  variant="body1"
                  align="center"
                  color="textSecondary"
                >
                  Please wait while we switch your organization.
                </Typography>
              </Box>
            </DialogContent>
          </>
        );

      case 'success':
        return (
          <>
            <DialogTitle>
              <Typography
                variant="h5"
                component="div"
                align="center"
                color="success.main"
              >
                Organization Switched Successfully
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap={2}
                sx={{ pt: 2, pb: 2 }}
              >
                <CheckCircle sx={{ fontSize: 60, color: 'success.main' }} />
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    p: 2,
                    backgroundColor: 'success.50',
                    borderRadius: 1,
                    border: 1,
                    borderColor: 'success.200',
                  }}
                >
                  <Business sx={{ color: 'success.main', fontSize: 24 }} />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 'medium',
                      color: 'success.main',
                    }}
                  >
                    {orgName
                      ? decodeURIComponent(orgName)
                      : 'Unknown Organization'}
                  </Typography>
                </Box>
                <Typography variant="body2" color="textSecondary">
                  Redirecting to home...
                </Typography>
              </Box>
            </DialogContent>
          </>
        );

      case 'error':
        return (
          <>
            <DialogTitle>
              <Typography
                variant="h5"
                component="div"
                align="center"
                color="error.main"
              >
                Failed to Switch Organization
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap={2}
                sx={{ pt: 2 }}
              >
                <Error sx={{ fontSize: 60, color: 'error.main' }} />
                {error && (
                  <Alert
                    severity="error"
                    sx={{ width: '100%', textAlign: 'left' }}
                  >
                    {error}
                  </Alert>
                )}
              </Box>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3 }}>
              <Button variant="outlined" onClick={handleClose}>
                Close
              </Button>
              <Button variant="contained" onClick={handleRetry}>
                Try Again
              </Button>
            </DialogActions>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={
        status === 'switching' || status === 'success' ? undefined : handleClose
      }
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={status === 'switching' || status === 'success'}
    >
      {renderContent()}
    </Dialog>
  );
};

export default OrganizationSwitching;
